import { ReactNode, useEffect } from "react";
import {
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { Column } from "../../components/Layout/Layout";
import { useSubNav } from "../../components/LoggedinPage/SubNavContext";
import { EditLocationModel } from "../../core/api/locations/types";
import useTranslations from "../../core/i18n/useTranslations";
import { SaveObjectProvider } from "../../core/SaveObjectContext/SaveObjectContext";
import useUser from "../../core/user/useUser";
import LoadingSpinner from "../../ui-lib/components/Loading/LoadingSpinner";
import PageHeader from "../../ui-lib/components/PageHeader/PageHeader";
import styles from "../PagesLayout.module.css";
import useSharedLocation from "./SharedLocationContext/useSharedLocationContext";
import LocationGeneral from "./LocationGeneral";
import { SharedLocationProvider } from "./SharedLocationContext/SharedLocationContext";
import LocationActions from "./LocationActions";
import User from "../../core/user/User";
import ObservationIdentifiers from "./LocationObservationIdentifiers/ObservationIdentifiers";

const parentPath = "/adminportal/locations";

const hasObservationIdentifiersAccess = (
  user: User,
  location?: EditLocationModel,
) =>
  user.hasAccess("EditKnownLocationDataDefinitions") &&
  location?.klType === "ROOM";

interface ISubNav {
  key: string;
  to: string;
}

const subMenuItems = (
  user: User,
  url: string,
  location?: EditLocationModel,
) => {
  const menu: ISubNav[] = [
    {
      key: "Menu:Location_details",
      to: `${url}`,
    },
    ...(hasObservationIdentifiersAccess(user, location)
      ? [
          {
            key: "Menu:Observation_identifiers",
            to: `${url}/room-identifiers`,
          },
        ]
      : []),
  ];
  return menu;
};

const LocationDetailsWrapper = ({
  data,
  children,
}: {
  data: EditLocationModel;
  children: ReactNode;
}) => {
  const t = useTranslations();

  return (
    <>
      <PageHeader
        title={t(`Locations:BeaconType_${data.klType}`)}
        icon="location-marker"
      >
        {data.id && <LocationActions />}
      </PageHeader>
      <Column className={styles.content} align="fill" type="top">
        {children}
      </Column>
    </>
  );
};

const LocationDetails = () => {
  const { path } = useRouteMatch();
  const { location } = useSharedLocation();
  const user = useUser();

  if (!location) {
    return <LoadingSpinner theme="primary" />;
  }

  return (
    <Switch>
      {hasObservationIdentifiersAccess(user, location) && (
        <Route path={`${path}/room-identifiers`}>
          <LocationDetailsWrapper data={location}>
            <ObservationIdentifiers />
          </LocationDetailsWrapper>
        </Route>
      )}
      <Route path={`${path}`}>
        <LocationDetailsWrapper data={location}>
          <LocationGeneral />
        </LocationDetailsWrapper>
      </Route>
    </Switch>
  );
};

const LocationDetailsLoader = () => {
  const t = useTranslations();
  const user = useUser();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { location, isError } = useSharedLocation();
  const { setSubNav } = useSubNav();

  useEffect(() => {
    setSubNav({
      menu: subMenuItems(user, url, location),
      backUrl: parentPath,
      backText: t("Menu:locations"),
    });
    return () => {
      setSubNav(undefined);
    };
  }, [setSubNav, location, url]);

  useEffect(() => {
    if (isError) {
      history.replace(parentPath);
    }
  }, [isError]);

  return <LocationDetails />;
};

const LocationDetailsContainer = () => {
  const { id } = useParams<{ id: string }>();

  return (
    <SharedLocationProvider locationId={id}>
      <SaveObjectProvider>
        <LocationDetailsLoader />
      </SaveObjectProvider>
    </SharedLocationProvider>
  );
};
export default LocationDetailsContainer;
